import { useState, useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, hexToRgba } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

gsap.registerPlugin(ScrollTrigger, SplitText);

// Random char set for scramble effect
const CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789@#$%&*';

function ScrambleText({ text, active, accent }) {
    const [display, setDisplay] = useState(text);
    const frameRef = useRef(null);
    const iterRef = useRef(0);

    useEffect(() => {
        if (!active) { setDisplay(text); return; }
        iterRef.current = 0;
        const total = text.length * 3;
        const tick = () => {
            iterRef.current++;
            const progress = iterRef.current / total;
            setDisplay(
                text.split('').map((char, i) => {
                    if (char === ' ') return ' ';
                    if (i / text.length < progress) return char;
                    return CHARS[Math.floor(Math.random() * CHARS.length)];
                }).join('')
            );
            if (iterRef.current < total) {
                frameRef.current = requestAnimationFrame(tick);
            } else {
                setDisplay(text);
            }
        };
        frameRef.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frameRef.current);
    }, [active, text]);

    return (
        <span style={{ color: accent, fontFamily: 'monospace' }}>{display}</span>
    );
}

function TypewriterWords({ words, accent }) {
    const [wordIdx, setWordIdx] = useState(0);
    const [charIdx, setCharIdx] = useState(0);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (!words || words.length === 0) return;
        const current = words[wordIdx] || '';
        const delay = deleting ? 60 : charIdx === current.length ? 1200 : 80;
        const timer = setTimeout(() => {
            if (!deleting && charIdx < current.length) {
                setCharIdx(c => c + 1);
            } else if (!deleting && charIdx === current.length) {
                setDeleting(true);
            } else if (deleting && charIdx > 0) {
                setCharIdx(c => c - 1);
            } else {
                setDeleting(false);
                setWordIdx(w => (w + 1) % words.length);
            }
        }, delay);
        return () => clearTimeout(timer);
    }, [charIdx, deleting, wordIdx, words]);

    if (!words || words.length === 0) return null;
    const current = (words[wordIdx] || '').slice(0, charIdx);

    return (
        <span style={{ color: accent, borderRight: `3px solid ${accent}`, paddingRight: 4, animation: 'blink-cursor 0.75s step-end infinite' }}>
            {current}
        </span>
    );
}

export default function KineticTextSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'reveal';

    const revealRef = useRef(null);
    const gradRef = useRef(null);
    const mainText = content.text || 'We Build The Future';
    const words = content.words || [];
    const accent = s.accent || primaryColor || '#6366f1';
    const textColor = s.text || '#ffffff';

    // GSAP SplitText animation for reveal variant
    useEffect(() => {
        if (variant !== 'reveal' || !revealRef.current || !visible) return;
        const ctx = gsap.context(() => {
            const split = SplitText.create(revealRef.current, { type: 'words' });
            gsap.from(split.words, {
                opacity: 0, y: 40, duration: 0.7, ease: 'power2.out',
                stagger: 0.08,
            });
        }, revealRef);
        return () => ctx.revert();
    }, [variant, visible]);

    // GSAP gradient background-position animation
    useEffect(() => {
        if (variant !== 'gradient' || !gradRef.current || !visible) return;
        const ctx = gsap.context(() => {
            gsap.to(gradRef.current, {
                backgroundPosition: '100% 50%',
                duration: 4,
                ease: 'sine.inOut',
                repeat: -1,
                yoyo: true,
            });
        }, gradRef);
        return () => ctx.revert();
    }, [variant, visible]);

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#0a0a0a', ...s.bgStyle };

    // ── Reveal: words fade in staggered on scroll (GSAP SplitText) ──
    if (variant === 'reveal') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="text-center py-8 overflow-hidden">
                        <h2
                            ref={revealRef}
                            className="text-5xl md:text-7xl lg:text-8xl font-black leading-none tracking-tight"
                            style={{ color: textColor, visibility: visible ? 'visible' : 'hidden' }}
                        >
                            {mainText}
                        </h2>
                        {words.length > 0 && (
                            <div
                                className="mt-6 text-3xl md:text-5xl lg:text-6xl font-bold"
                                style={{
                                    opacity: visible ? 1 : 0,
                                    transition: 'opacity 0.7s ease 0.6s',
                                }}
                            >
                                <TypewriterWords words={words} accent={accent} />
                            </div>
                        )}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Scramble: letters decode on scroll ──
    if (variant === 'scramble') {
        const wordList = mainText.split(' ');
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="text-center py-8">
                        <h2 className="text-5xl md:text-7xl lg:text-8xl font-black leading-none tracking-tight break-words">
                            {wordList.map((word, i) => (
                                <span key={i} style={{ display: 'inline-block', marginRight: '0.3em' }}>
                                    <ScrambleText text={word} active={visible} accent={i % 3 === 0 ? accent : textColor} />
                                </span>
                            ))}
                        </h2>
                        {words.length > 0 && (
                            <div className="mt-6 text-3xl md:text-5xl font-bold" style={{ color: textColor }}>
                                <TypewriterWords words={words} accent={accent} />
                            </div>
                        )}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Gradient: animated gradient background-clip text ──
    if (variant === 'gradient') {
        const gradFrom = accent;
        const gradTo = secondaryColor || '#ec4899';
        return (
            <section className={cls} style={bgS}>
                <style>{`
                    @keyframes blink-cursor {
                        from, to { border-color: transparent; }
                        50%      { border-color: currentColor; }
                    }
                `}</style>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="text-center py-8">
                        <h2
                            ref={gradRef}
                            className="text-5xl md:text-7xl lg:text-8xl font-black leading-none tracking-tight"
                            style={{
                                backgroundImage: `linear-gradient(270deg, ${gradFrom}, ${gradTo}, ${hexToRgba(gradFrom, 0.8)}, ${gradTo})`,
                                backgroundSize: '400% 400%',
                                backgroundPosition: '0% 50%',
                                WebkitBackgroundClip: 'text',
                                backgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                            }}
                        >
                            {mainText}
                        </h2>
                        {words.length > 0 && (
                            <div className="mt-6 text-3xl md:text-5xl font-bold" style={{ color: textColor }}>
                                <TypewriterWords words={words} accent={accent} />
                            </div>
                        )}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Split: words spread apart and converge on scroll ──
    // variant === 'split'
    const splitWords = mainText.split(' ');
    const mid = Math.ceil(splitWords.length / 2);
    const leftWords = splitWords.slice(0, mid);
    const rightWords = splitWords.slice(mid);

    return (
        <section className={cls} style={bgS}>
            <style>{`
                @keyframes blink-cursor {
                    from, to { border-color: transparent; }
                    50%      { border-color: currentColor; }
                }
            `}</style>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                <div className="text-center py-8 overflow-hidden">
                    <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
                        {leftWords.map((word, i) => (
                            <span
                                key={`l-${i}`}
                                className="inline-block text-5xl md:text-7xl lg:text-8xl font-black leading-none"
                                style={{
                                    color: i === leftWords.length - 1 ? accent : textColor,
                                    opacity: visible ? 1 : 0,
                                    transform: visible ? 'translateX(0)' : `translateX(-${60 + i * 20}px)`,
                                    transition: `opacity 0.8s ease ${i * 100}ms, transform 0.8s cubic-bezier(0.16,1,0.3,1) ${i * 100}ms`,
                                }}
                            >
                                {word}
                            </span>
                        ))}
                        {rightWords.map((word, i) => (
                            <span
                                key={`r-${i}`}
                                className="inline-block text-5xl md:text-7xl lg:text-8xl font-black leading-none"
                                style={{
                                    color: i === 0 ? accent : textColor,
                                    opacity: visible ? 1 : 0,
                                    transform: visible ? 'translateX(0)' : `translateX(${60 + i * 20}px)`,
                                    transition: `opacity 0.8s ease ${(i + mid) * 100}ms, transform 0.8s cubic-bezier(0.16,1,0.3,1) ${(i + mid) * 100}ms`,
                                }}
                            >
                                {word}
                            </span>
                        ))}
                    </div>
                    {words.length > 0 && (
                        <div
                            className="mt-6 text-3xl md:text-5xl font-bold"
                            style={{
                                color: textColor,
                                opacity: visible ? 1 : 0,
                                transition: `opacity 0.8s ease ${splitWords.length * 100 + 300}ms`,
                            }}
                        >
                            <TypewriterWords words={words} accent={accent} />
                        </div>
                    )}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
